import { Injectable } from '@angular/core';
import { TAppDetailsCpi } from '@monorepo/types';
import { select, Store } from '@ngrx/store';
import { combineLatest, from } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { AuthService } from '../../authentication/auth.service';
import { TInputAppState } from '../reducer';
import { AppSwitch, ChangeStation, LoadAppMasterDB } from './app-master-db.actions';
import {
  allAppsInfo,
  cutbankInModule,
  cutbankInViewDescriptors,
  cutbankOutModule,
  cutbankOutViewDescriptors,
  embellishmentInModule,
  embellishmentInViewDescriptors,
  embellishmentOutModule,
  embellishmentOutViewDescriptors,
  getAllDashboards,
  getAllStationIds,
  getAllStations,
  getAllStationsV2,
  getAppDetailsWithAdditionalInfo,
  getAppDetailsWithAdditionalInfoForWashing,
  getAppIdentifier,
  getAppName,
  getAppSyncItemList,
  getAppTypesForEmbellishment,
  getBctxItemsOrderWithoutSize,
  getBctxViz,
  getBuzKeys,
  getConfigurations,
  getLineInTransactionsViewDescriptor,
  getMemberAdditionViewDescriptor,
  getPerPieceLineInTransactionsViewDescriptor,
  getSelectedModuleForApp,
  getSubjectKey,
  getSubjectKeyLandingPageAndCurrentModule,
  getSubjectKeyWithMasterdataListAndConfs,
  getTokenAndSelectedSubject,
  getWashingTransactionsViewDescriptor,
  isStationTypeWipEnd,
  selectAppDetails,
  selectAppInfo,
  selectClientInfo,
  selectDeviceInfo,
  selectedAllViewDescriptor,
  selectedApp,
  selectedAppSettings,
  selectedBusinessContext,
  selectedHasSilhouette,
  selectedLandingViewDescriptor,
  selectedModule,
  selectedSubject,
  bundleLineInModule,
  bundleLineInViewDescriptors,
} from './app-master.query';

@Injectable({
  providedIn: 'root',
})
export class AppMasterDBFacade {
  constructor(private store: Store<TInputAppState>, private authService: AuthService) {}

  loadAllDatabases() {
    this.store.dispatch(new LoadAppMasterDB());
  }

  getTokenAndSelectedSubject() {
    return this.store.pipe(select(getTokenAndSelectedSubject));
  }

  getAppName() {
    return this.store.pipe(select(getAppName));
  }

  getAppIdentifier() {
    return this.store.pipe(select(getAppIdentifier));
  }

  getAllDashboards() {
    return this.store.pipe(select(getAllDashboards));
  }

  getAppSettings() {
    return this.store.pipe(select(selectedAppSettings));
  }

  getAppInfo() {
    return this.store.pipe(select(selectAppInfo));
  }

  getAllAppsInfo() {
    return this.store.pipe(select(allAppsInfo));
  }

  // getAllAppsInfo(){
  //   return this.store.pipe(select(allAppsInfoV2))
  // }
  // TODO NISHA remove platform : undefined after platform is added to the app details backend
  getAppDetails() {
    return combineLatest([
      from(this.authService.getDecodedJWTPromise()),
      this.store.pipe(select(selectAppDetails)),
    ]).pipe(
      take(1),
      map(([decodedJW, appDetails]) => {
        return {
          ...appDetails,
          platform: undefined,
          urId: decodedJW ? decodedJW.usr : '',
          urNm: decodedJW ? decodedJW.name : '',
        };
      }),
    );
  }

  getAppDetailsWithPlatForm() {
    return combineLatest([
      from(this.authService.getDecodedJWTPromise()),
      this.store.pipe(select(selectAppDetails)),
    ]).pipe(
      take(1),
      map(([decodedJW, appDetails]) => {
        return {
          ...appDetails,
          urId: decodedJW ? decodedJW.usr : '',
          urNm: decodedJW ? decodedJW.name : '',
        };
      }),
    );
  }

  // getAppDetails() + additionalInfo
  getAppDetailsCPI() {
    return combineLatest([this.getAppDetails(), this.store.pipe(select(getAppDetailsWithAdditionalInfo))]).pipe(
      take(1),
      map(([appDetails, additonalInfo]) => {
        const { dnrk, dnm, snm, dnr } = additonalInfo;
        const appDetailsCpi: TAppDetailsCpi = {
          ...appDetails,
          dnrk,
          dnm,
          snm,
          dnr,
        };
        return appDetailsCpi;
      }),
    );
  }

  getAppDetailsWithAdditionalInfo() {
    return combineLatest([
      from(this.authService.getDecodedJWTPromise()),
      this.store.pipe(select(getAppDetailsWithAdditionalInfo)),
    ]).pipe(
      take(1),
      map(([decodedJW, appDetails]) => {
        console.log('decodedJW', decodedJW);
        return {
          ...appDetails,
          urId: decodedJW.usr,
          urNm: decodedJW.name,
          roles: decodedJW.roles,
        };
      }),
    );
  }

  getWashingAppDetailsWithAdditionalInfo() {
    return combineLatest([
      from(this.authService.getDecodedJWTPromise()),
      this.store.pipe(select(getAppDetailsWithAdditionalInfoForWashing)),
    ]).pipe(
      take(1),
      map(([decodedJW, appDetails]) => {
        return {
          ...appDetails,
          urId: decodedJW.usr,
          urNm: decodedJW.name,
        };
      }),
    );
  }

  getAppDetailsForEmbellishmentApp() {
    return combineLatest([
      from(this.authService.getDecodedJWTPromise()),
      this.store.pipe(select(getAppTypesForEmbellishment)),
    ]).pipe(
      take(1),
      map(([decodedJW, embAppDetails]) => {
        return {
          ...embAppDetails,
          urId: decodedJW ? decodedJW.usr : '',
          urNm: decodedJW ? decodedJW.first_name : '',
        };
      }),
    );
  }

  getAppDetailsForLogger() {
    return this.store.pipe(select(getAppDetailsWithAdditionalInfo));
  }

  getSelectedApp() {
    return this.store.pipe(select(selectedApp));
  }

  getSubjectKey() {
    return this.store.pipe(select(getSubjectKey));
  }

  getAllViewDescriptor() {
    return this.store.pipe(select(selectedAllViewDescriptor));
  }

  getCutbankInViewDescriptor() {
    return this.store.pipe(select(cutbankInViewDescriptors));
  }

  getCutbankInModule() {
    return this.store.pipe(select(cutbankInModule));
  }

  getCutbankOutModule() {
    return this.store.pipe(select(cutbankOutModule));
  }

  getCutbankOutViewDescriptor() {
    return this.store.pipe(select(cutbankOutViewDescriptors));
  }

  getEmbellishmentInViewDescriptor() {
    return this.store.pipe(select(embellishmentInViewDescriptors));
  }

  getEmbellishmentOutViewDescriptor() {
    return this.store.pipe(select(embellishmentOutViewDescriptors));
  }

  getEmbellishmentInModule() {
    return this.store.pipe(select(embellishmentInModule));
  }
  getEmbellishmentOutModule() {
    return this.store.pipe(select(embellishmentOutModule));
  }
  getBundleLineInModule() {
    return this.store.pipe(select(bundleLineInModule));
  }

  getBundleLineInViewDescriptor() {
    return this.store.pipe(select(bundleLineInViewDescriptors));
  }

  getLandingPage() {
    return this.store.pipe(select(selectedLandingViewDescriptor));
  }

  getSubjectKeyLandingPageAndCurrentModule() {
    return this.store.pipe(select(getSubjectKeyLandingPageAndCurrentModule));
  }

  getSubjectKeyWithMasterdataListAndConfs() {
    return this.store.pipe(select(getSubjectKeyWithMasterdataListAndConfs));
  }

  getSelectedStation() {
    return this.store.pipe(select(selectedSubject));
  }

  getHasSilhouette() {
    return this.store.pipe(select(selectedHasSilhouette));
  }

  getClientInfo() {
    return this.store.pipe(select(selectClientInfo));
  }

  getDeviceInfo() {
    return this.store.pipe(select(selectDeviceInfo));
  }

  getAllStationIds() {
    return this.store.pipe(select(getAllStationIds));
  }

  getAllStations() {
    return this.store.pipe(select(getAllStations));
  }

  getAllStationsV2() {
    return this.store.pipe(select(getAllStationsV2));
  }

  getSelectedBusinessContext() {
    return this.store.pipe(select(selectedBusinessContext));
  }

  getSelectedModule() {
    return this.store.pipe(select(selectedModule));
  }

  // Change

  changeStation(stationId: string) {
    this.store.dispatch(new ChangeStation(stationId));
  }

  appSwitch(appId: string) {
    this.store.dispatch(new AppSwitch(appId));
  }

  getAppSyncItemList() {
    return this.store.pipe(select(getAppSyncItemList));
  }

  getMemberAdditionList() {
    return this.store.pipe(select(getMemberAdditionViewDescriptor));
  }

  getPerPieceLineInTransactionsViewDescriptor() {
    return this.store.pipe(select(getPerPieceLineInTransactionsViewDescriptor));
  }

  getLineInTransactionsViewDescriptor() {
    return this.store.pipe(select(getLineInTransactionsViewDescriptor));
  }

  getWashingTransactionsViewDescriptor() {
    return this.store.pipe(select(getWashingTransactionsViewDescriptor));
  }

  getBuzKeys() {
    return this.store.pipe(select(getBuzKeys));
  }

  getBctxViz() {
    return this.store.pipe(select(getBctxViz));
  }

  getBctxItemsOrderWithoutSize() {
    return this.store.pipe(select(getBctxItemsOrderWithoutSize));
  }

  getSelectedModuleForApp() {
    return this.store.pipe(select(getSelectedModuleForApp));
  }

  getConfigurations() {
    return this.store.pipe(select(getConfigurations));
  }

  isStationTypeWipEnd() {
    return this.store.pipe(select(isStationTypeWipEnd));
  }
}

export type TGetAllViewDescriptors = ReturnType<AppMasterDBFacade['getAllViewDescriptor']>;
export type TGetClientInfo = ReturnType<AppMasterDBFacade['getClientInfo']>;
export type TGetAppInfo = ReturnType<AppMasterDBFacade['getAppInfo']>;
export type TGetAppName = ReturnType<AppMasterDBFacade['getAppName']>;
export type TGetAppIdentifier = ReturnType<AppMasterDBFacade['getAppIdentifier']>;
